import ks from "../ReUsableTS/kBrowserShared";
import { khtmlButton } from "../ReUsableTS/kHTML";
import LNG from "../ReUsableTS/kLNG";
import { kPhrase } from "../ReUsableTS/kPhrase";
import { kMultiSelectOption, kMultiSelectOptionType } from "../ReUsableTS/kMultiSelectOption";
import type { kHTMLMultiSelect } from "./kHTMLMultiSelect";
/**
 * The panel chrome builders of a kHTMLMultiSelect: search row, Clear link, All row, one choice
 * row and the OK button. Split out of kHTMLMultiSelect.ts for size; kHTMLMultiSelect keeps
 * same-signature delegating members. Error-handler labels are unchanged.
 */
export class kHTMLMultiSelectChrome {
    private Owner: kHTMLMultiSelect;
    constructor(Owner: kHTMLMultiSelect) {
        this.Owner = Owner;
    }
    public BuildSearchRow = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            // ToDiv creates ListWrapper immediately before calling this and the four Build*
            // methods below, so in the shipped call order it is never null here. The guard is
            // what makes that ORDER explicit rather than assumed — and it is the same one this
            // file already uses at ExpandListWrapper and AddChoice. Without it the five methods
            // dereferenced a `HTMLDivElement | null` field outright, which broke the type check
            // of every consuming product (found 2026-08-07 building Templates Manager).
            if (this.Owner.ListWrapper === null) { return; }
            this.Owner.SearchInput = document.createElement("input");
            this.Owner.SearchInput.classList.add("multiselect-dropdown-search");
            this.Owner.SearchInput.classList.add("form-control");
            this.Owner.SearchInput.placeholder = LNG.SEARCH;
            this.Owner.SearchInput.style.width = this.Owner.Width;
            this.Owner.SearchInput.addEventListener("input", this.Owner.SearchInputChanged);
            // `Search = false` HIDES the box; it must not skip building it. ExpandListWrapper,
            // DocumentClick, SetSelectedChoices and SearchInputChanged all guard on
            // the owner's `SearchInput` being non-null, so a control built without one could never be
            // OPENED again — a far worse defect than the one being fixed. This is also what the
            // original widget did (see the commented-out source at the foot of kHTMLMultiSelect.ts).
            // Until 2026-08-14 `Search` was declared, defaulted to true and never read at all,
            // so the box always rendered; the default keeps that behaviour for every caller.
            if (this.Owner.Search === false) { this.Owner.SearchInput.style.display = "none"; }
            this.Owner.ListWrapper.appendChild(this.Owner.SearchInput);
            this.BuildClearLink();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHTMLMultiSelectChrome.BuildSearchRow");
        }
    };

    /**
     * The "× Clear" link — deselects everything without closing the panel.
     *
     * ⚠ ITS CAPTION WAS A HARD-CODED ENGLISH LITERAL UNTIL 2026-08-17, so it read "Clear" on a
     * Swedish, German or Arabic screen while all four of its siblings in this control
     * (LNG.SEARCH / ALL / OK / ADDNEW) were translated. Reported by the owner against a live
     * MOSE dashboard.
     *
     * ⚠ THE READ GOES THROUGH kPhrase BECAUSE THIS IS THE FIRST REFERENCE TO LNG.CLEAR FROM
     * SHARED CODE. Three of the five product kLNG snapshots in the estate did not carry the key,
     * and an unguarded read puts the literal word "undefined" on the button with the type check,
     * the unit suites and the build all green. See kPhrase for the measurement and the mechanism.
     *
     * Split out of BuildSearchRow the same day: documenting the above took that method past the
     * 40-line cap, and the two really are separate things sharing a row.
     */
    public BuildClearLink = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Owner.ListWrapper === null) { return; }   // see BuildSearchRow
            const ClearLink: HTMLSpanElement = document.createElement("span");
            ClearLink.textContent = "× " + kPhrase.Or(LNG.CLEAR, "Clear");
            ClearLink.style.cssText = "float:right;cursor:pointer;font-size:11px;color:#7B61FF;padding:4px 8px;user-select:none;font-weight:600;";
            ClearLink.addEventListener("click", this.Owner.DeSelectAll);
            this.Owner.ListWrapper.appendChild(ClearLink);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHTMLMultiSelectChrome.BuildClearLink");
        }
    };

    public BuildAllOption = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (!this.Owner.ShowAllOption) { return; }
            if (this.Owner.ListWrapper === null) { return; }   // see BuildSearchRow
            this.Owner.AllOption = new kMultiSelectOption();
            this.Owner.AllOption.Text = LNG.ALL;
            this.Owner.AllOption.Parent = this.Owner;
            this.Owner.AllOption.OptionType = kMultiSelectOptionType.All;
            this.Owner.AllOption.ToAvailableOption();
            if (this.Owner.AllOption.AvailableDiv) {
                this.Owner.AllOption.AvailableDiv.className = "multiselect-dropdown-all-selector";
                this.Owner.ListWrapper.appendChild(this.Owner.AllOption.AvailableDiv);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHTMLMultiSelectChrome.BuildAllOption");
        }
    };

    /** One available-choice row: build the option for Text, parent it, render its div and insert
        it before Ref (null = append at the end). Returns the new option; the caller registers it
        in ChoiceOptions (and in Choices where that list is the render source). The
        KGlobalErrorHandler label is always the caller's own. */
    public AppendChoiceRow = (Text: string, Ref: HTMLElement | null, Label: string): kMultiSelectOption => {
        if (ks.KErrorMode === true) { return new kMultiSelectOption(); }
        try {
            const NewOption: kMultiSelectOption = new kMultiSelectOption();
            NewOption.Text = Text;
            NewOption.Parent = this.Owner;
            const Div: HTMLDivElement = NewOption.ToAvailableOption();
            if (this.Owner.ListWrapper !== null) {
                if (Ref !== null) { this.Owner.ListWrapper.insertBefore(Div, Ref); }
                else { this.Owner.ListWrapper.appendChild(Div); }
            }
            return NewOption;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHTMLMultiSelectChrome.AppendChoiceRow");
            return new kMultiSelectOption();
        }
    };

    public BuildOkButton = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Owner.ListWrapper === null) { return; }   // see BuildSearchRow
            this.Owner.OKButton = new khtmlButton();
            this.Owner.OKButton.SetText(LNG.OK);
            this.Owner.OKButton.SetCallBack(this.Owner.OKClick);
            if (!this.Owner.OKButton.Button) { return; }
            const OKBtn: HTMLButtonElement = this.Owner.OKButton.Button;
            OKBtn.style.cssFloat = "right";
            OKBtn.style.margin = "8px";
            OKBtn.style.padding = "6px 16px";
            OKBtn.style.fontSize = "12px";
            OKBtn.style.fontWeight = "600";
            OKBtn.style.background = "#7B61FF";
            OKBtn.style.color = "white";
            OKBtn.style.border = "none";
            OKBtn.style.borderRadius = "8px";
            OKBtn.style.cursor = "pointer";
            OKBtn.style.boxShadow = "none";
            OKBtn.style.transform = "none";
            OKBtn.style.transition = "background 150ms";
            this.Owner.ListWrapper.appendChild(OKBtn);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHTMLMultiSelectChrome.BuildOkButton");
        }
    };
}
